import React, { useState, useEffect, useCallback } from 'react';
import './Listings.css';
import { CircularProgress, Pagination, IconButton, Drawer, Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import FilterAccordion from './components/FilterAccordion';
import ListingCard from './components/ListingCard';
import SortBySelect from './components/SortBySelect';
import { listings } from './data/listings';
import applyFiltersAndSort from './utils/applyFiltersAndSort';
import { schoolMapping } from './utils/schoolMapping';
import useWindowSize from '../../Globals/Helpers/useWindowSize';
import AddIcon from '@mui/icons-material/Add';

const ITEMS_PER_PAGE = 10;

export default function Listings() {
  const [searchParams] = useSearchParams();
  const { width } = useWindowSize();
  const isMobile = width <= 640;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('Price Low to High');
  const [priceRange, setPriceRange] = useState([0, 250]);
  const [conditions, setConditions] = useState([]);
  const [bookTypes, setBookTypes] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [courseCode, setCourseCode] = useState('all');
  const [options, setOptions] = useState([]);  // For course code options
  const [page, setPage] = useState(1);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = useCallback((searchValue) => {
    console.log("Search triggered with:", searchValue);
    if (!searchValue?.trim()) {
      setSearchQuery('');
      setSearchResults([]);
      const filtered = applyFiltersAndSort(
        listings,
        priceRange,
        conditions,
        bookTypes,
        selectedSchools,
        sortBy,
        courseCode
      );
      setFilteredListings(filtered);
      return;
    }

    setSearchQuery(searchValue.trim()); // Store the search query
    
    const results = listings.filter(book => {
      if (!book.title && !book.author) return false;
      
      return (
        book.title.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
        book.author.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
        book.courseCode.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
        (book.isbn && book.isbn.toString().includes(searchValue))
      );
    });

    setSearchResults(results);
    setFilteredListings(results);
  }, [priceRange, conditions, bookTypes, selectedSchools, sortBy, courseCode]);

  // Initial load effect
  useEffect(() => {
    const query = searchParams.get('search');
    if (query) {
      setSearchQuery(query);
      const searchLower = query.toLowerCase().trim();
      const results = listings.filter(book => {
        if (!book.title && !book.author) return false;
        return (
          book.title.toLowerCase().includes(searchLower) ||
          book.author.toLowerCase().includes(searchLower) ||
          book.courseCode.toLowerCase().includes(searchLower) ||
          (book.isbn && book.isbn.toString().includes(query))
        );
      });
      setSearchResults(results);
      setFilteredListings(results);
    }
  }, [searchParams]);

  // Separate effect for filter changes
  useEffect(() => {
    if (searchResults.length > 0) {
      // If we have search results, apply filters to those
      const filtered = applyFiltersAndSort(
        searchResults,
        priceRange,
        conditions,
        bookTypes,
        selectedSchools,
        sortBy,
        courseCode
      );
      setFilteredListings(filtered);
    } else {
      // If no search results, apply filters to all listings
      const filtered = applyFiltersAndSort(
        listings,
        priceRange,
        conditions,
        bookTypes,
        selectedSchools,
        sortBy,
        courseCode
      );
      setFilteredListings(filtered);
    }
  }, [priceRange, conditions, bookTypes, selectedSchools, sortBy, courseCode, searchResults]);

  // Update the URL params effect
  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      handleSearch(searchQuery);
    }
  }, [searchParams, handleSearch]); // Add handleSearch to dependencies

  // Make handleSearch available globally for the navbar
  useEffect(() => {
    window.handleSearch = handleSearch;
  }, [handleSearch]);

  const handleConditionChange = (condition, isChecked) => {
    setConditions(prev => 
      isChecked ? [...prev, condition] : prev.filter(c => c !== condition)
    );
  };

  const handleBookTypeChange = (type, isChecked) => {
    setBookTypes(prev => 
      isChecked ? [...prev, type] : prev.filter(t => t !== type)
    );
  };

  const handleSchoolChange = (school, isChecked) => {
    setSelectedSchools(prev => 
      isChecked ? [...prev, school] : prev.filter(s => s !== school)
    );
  };

  useEffect(() => {
    // Extract unique course codes from listings
    const uniqueCodes = [...new Set(listings.map(listing => listing.courseCode))];
    setOptions(uniqueCodes.filter(Boolean));  // Remove any undefined/null values
  }, []);

  const handlePageChange = (event, value) => setPage(value);

  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
  };

  return (
    <div className="listings-container">
      {/* Mobile Filter Toggle Button */}
      {isMobile && (
        <IconButton
          onClick={toggleMobileFilters}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: '#6B89FF',
            color: 'white',
            width: 56,
            height: 56,
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(107, 137, 255, 0.3)',
            '&:hover': {
              backgroundColor: '#5A75E6',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon />
        </IconButton>
      )}

      {/* Mobile Filters Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileFiltersOpen}
          onClose={toggleMobileFilters}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: '80vh',
            }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Filters
            </Typography>
            <FilterAccordion
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              handleConditionChange={handleConditionChange}
              handleBookTypeChange={handleBookTypeChange}
              handleSchoolChange={handleSchoolChange}
              courseCode={courseCode}
              setCourseCode={setCourseCode}
              options={options}
              schoolMapping={schoolMapping}
              conditions={conditions}
              bookTypes={bookTypes}
              selectedSchools={selectedSchools}
            />
          </Box>
        </Drawer>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className="listings-sidebar">
          <FilterAccordion
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            handleConditionChange={handleConditionChange}
            handleBookTypeChange={handleBookTypeChange}
            handleSchoolChange={handleSchoolChange}
            courseCode={courseCode}
            setCourseCode={setCourseCode}
            options={options}
            schoolMapping={schoolMapping}
            conditions={conditions}
            bookTypes={bookTypes}
            selectedSchools={selectedSchools}
          />
        </aside>
      )}

      {true ? (
        <div className="content">
          <div className="list-wrapper">
            <div className="listings-header">
              <div className="listings-count">
                {searchQuery ? (
                  <p>{filteredListings.length} results for "{searchQuery}"</p>
                ) : (
                  <p>{filteredListings.length} results</p>
                )}
              </div>
              <div className="sort-select">
                <SortBySelect sortBy={sortBy} setSortBy={setSortBy} />
              </div>
            </div>

            <div className="list listings-wrapper">
              {filteredListings.length > 0 ? (
                filteredListings
                  .slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
                  .map((listing, index) => (
                    <ListingCard key={index} listing={listing} />
                  ))
              ) : (
                <div className="card-wrapper">
                  <div className="card">
                    <div className="info">
                      <h4 className="title">No match found for the chosen filters</h4>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="pagination-container">
              <Pagination
                className="styled-pagination"
                count={Math.ceil(filteredListings.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          </div>
        </div>
      ) : (
        <CircularProgress />
      )}
    </div>
  );
}